<script>
  import { onMount } from 'svelte';
  import { fly, fade } from 'svelte/transition';
  import { db, initializeMeta, seedDemoData } from './lib/db.js';
  import { createUuid } from './lib/uuid.js';
  import { errorCount, isSyncConfigured, pendingCount, startAutomaticSync, syncNow } from './lib/sync.js';
  import { buildTicketTspl } from './lib/tspl.js';

  let ready = false, fatalError = '', syncError = '', success = '';
  let errorDetailOpen = false, errorDetailText = '', errorDetailCopied = false;
  function showErrorDetail(text) { errorDetailText = text; errorDetailCopied = false; errorDetailOpen = true; }
  async function copyErrorDetail() {
    try { await navigator.clipboard.writeText(errorDetailText); errorDetailCopied = true; }
    catch { /* clipboard indisponible (contexte non sécurisé) : rien à faire, le texte reste sélectionnable */ }
  }
  let step = 1, pending = 0, errored = 0, lastSync = '';
  let forcingSync = false;
  let employees = [], zones = [], units = [], categories = [], variantTags = [], products = [];

  let employeeId = '';
  let zoneQuery = '', selectedZone = null;
  let descriptionSale = '', descriptionPurchase = '', reference = '', categoryName = '', unitName = 'Pièce';
  let lastCategory = '';
  const DEFAULT_UNIT = 'Pièce';
  let variantQuery = '', selectedVariants = [];
  let variantRows = [];
  let openList = '';

  // paramètres d'impression (dimensions ticket + relais centralisé, réglés par
  // l'admin — voir apps/admin) : valeurs de repli tant qu'une première
  // synchronisation n'a pas rempli db.parametres.
  let printSettings = {
    ticket_largeur_mm: 50, ticket_hauteur_mm: 25,
    tspl_largeur_mm: 50, tspl_hauteur_mm: 25, tspl_barcode_decalage_x: 0,
    tspl_largeur_caractere: 12,
    relais_ip: '', relais_port: 9201,
  };

  $: selectedEmployee = employees.find((item) => item.id === Number(employeeId));
  $: productValid = descriptionSale.trim() && categoryName.trim() && unitName.trim();
  // Toutes les lignes restent saisissables : une ligne déjà imprimée accepte
  // les ajouts (la part imprimée est tracée côté serveur, seule la différence
  // sera imprimée ensuite).
  $: editableRows = variantRows;
  $: rowsValid = editableRows.length > 0 && editableRows.every((row) =>
    Number.isFinite(Number(row.quantity)) && Number(row.quantity) !== 0 &&
    Math.abs(Number(row.quantity)) <= 1000000 &&
    Number(row.priceSale || 0) >= 0 && Number(row.pricePurchase || 0) >= 0);

  const norm = (value) => (value || '').trim().toLocaleLowerCase('fr');
  const contains = (haystack, needle) => norm(haystack).includes(norm(needle));

  const ACCENT_MAP = { à: 'a', â: 'a', ä: 'a', é: 'e', è: 'e', ê: 'e', ë: 'e', î: 'i', ï: 'i', ô: 'o', ö: 'o', ù: 'u', û: 'u', ü: 'u', ç: 'c', œ: 'oe', æ: 'ae' };
  function foldAccents(text) {
    return text.replace(/[àâäéèêëîïôöùûüçœæ]/gi, (ch) => ACCENT_MAP[ch.toLowerCase()] || ch);
  }

  function generateReference(description) {
    const base = foldAccents(description || 'PRODUIT')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .split('-')
      .filter(Boolean)
      .slice(0, 3)
      .join('-')
      .slice(0, 20);
    const suffix = Math.floor(1000 + Math.random() * 9000);
    return `${base || 'PRODUIT'}-${suffix}`;
  }

  $: zoneSuggestions = zoneQuery.trim()
    ? zones.filter((zone) => contains(zone.code, zoneQuery) || contains(zone.name, zoneQuery)).slice(0, 8)
    : zones.slice(0, 8);
  $: zoneExact = zones.find((zone) => norm(zone.code) === norm(zoneQuery) || norm(zone.name) === norm(zoneQuery));
  $: descSuggestions = descriptionSale.trim()
    ? products.filter((product) => contains(product.description_vente, descriptionSale)).slice(0, 8)
    : [];
  $: refSuggestions = reference.trim()
    ? products.filter((product) => contains(product.reference, reference)).slice(0, 8)
    : [];
  $: unitSuggestions = units.filter((unit) => !unitName.trim() || contains(unit.name, unitName)).slice(0, 8);
  $: catSuggestions = categories.filter((cat) => !categoryName.trim() || contains(cat.name, categoryName)).slice(0, 8);
  $: variantSuggestions = variantTags
    .filter((tag) => !selectedVariants.includes(tag.name) && (!variantQuery.trim() || contains(tag.name, variantQuery)))
    .slice(0, 8);
  $: variantCreatable = variantQuery.trim() &&
    !variantTags.some((tag) => norm(tag.name) === norm(variantQuery)) &&
    !selectedVariants.some((name) => norm(name) === norm(variantQuery));

  onMount(() => {
    let stopSync = () => {};
    (async () => {
      try {
        await initializeMeta(); await seedDemoData(); await refreshLocalState();
        const saved = await db.meta.get('current_employee_id');
        if (saved?.value && employees.some((item) => item.id === Number(saved.value))) {
          employeeId = String(saved.value);
          step = 2;
        }
        const savedCategory = await db.meta.get('last_category_name');
        if (savedCategory?.value) { lastCategory = savedCategory.value; categoryName = lastCategory; }
        ready = true;
        stopSync = startAutomaticSync(async (error) => {
          syncError = error?.message || '';
          await refreshLocalState();
        });
      } catch (error) { fatalError = error instanceof Error ? error.message : 'Stockage local indisponible'; }
    })();
    return () => stopSync();
  });

  async function refreshLocalState() {
    [employees, zones, units, categories, variantTags, products, pending, errored] = await Promise.all([
      db.employes.filter((item) => item.active !== false).sortBy('name'),
      db.zones.filter((item) => item.active !== false).sortBy('code'),
      db.unites.filter((item) => item.active !== false).sortBy('name'),
      db.categories.filter((item) => item.active !== false).sortBy('name'),
      db.variantes.filter((item) => item.active !== false).sortBy('name'),
      db.produits.filter((item) => item.active !== false).limit(500).toArray(),
      pendingCount(),
      errorCount(),
    ]);
    const settings = await db.parametres.toArray();
    if (settings.length) printSettings = settings[0];
    lastSync = (await db.meta.get('last_sync_at'))?.value || '';
    // Une zone créée hors-ligne vient d'être synchronisée : basculer sur la vraie.
    if (selectedZone?.local) {
      const real = zones.find((zone) => norm(zone.code) === norm(selectedZone.code));
      if (real) selectedZone = real;
    }
  }

  async function showErroredDetail() {
    const rows = await db.mouvements.where('statut').equals('erreur').toArray();
    const lines = rows.map((row) => `• ${row.produit?.description_vente || row.produit?.reference || row.produit_variante_id || '?'} : ${row.erreur_message || 'Rejeté par le serveur.'}`);
    showErrorDetail(lines.join('\n') || 'Aucun détail disponible.');
  }

  async function forceSyncNow() {
    if (forcingSync || !isSyncConfigured()) return;
    forcingSync = true;
    try { await syncNow(); syncError = ''; await refreshLocalState(); }
    catch (error) { syncError = error.message; }
    finally { forcingSync = false; }
  }

  async function confirmEmployee() {
    await db.meta.put({ key: 'current_employee_id', value: Number(employeeId) });
    success = '';
    step = 2;
  }

  function changeEmployee() { step = 1; openList = ''; }

  function changeZone() {
    // Les saisies déjà sauvegardées restent liées à leur zone d'origine —
    // on repart simplement sur un choix de zone vierge.
    zoneQuery = '';
    selectedZone = null;
    openList = '';
    step = 2;
  }

  function pickZone(zone) {
    selectedZone = zone;
    zoneQuery = zone.code;
    openList = '';
    step = 3;
  }

  function chooseZone() {
    const needle = norm(zoneQuery);
    const zone = zones.find((item) => norm(item.code) === needle || norm(item.name) === needle);
    if (zone) pickZone(zone);
    else selectedZone = null;
  }

  function createLocalZone() {
    const code = zoneQuery.trim();
    if (!code) return;
    // Zone créée sur le terrain : envoyée par code à la synchronisation,
    // le serveur la crée (ou la retrouve) — voir contrôleur sync.
    selectedZone = { id: null, code, name: code, local: true };
    openList = '';
    step = 3;
  }

  function zoneFields() {
    return selectedZone.local
      ? { zone_code: selectedZone.code, zone_nom: selectedZone.name }
      : { zone_id: selectedZone.id };
  }

  async function pickProduct(product) {
    reference = product.reference || '';
    descriptionSale = product.description_vente || '';
    descriptionPurchase = product.description_achat || '';
    const catId = Array.isArray(product.categorie_id) ? product.categorie_id[0] : product.categorie_id;
    const unitId = Array.isArray(product.unite_mesure_id) ? product.unite_mesure_id[0] : product.unite_mesure_id;
    categoryName = (await db.categories.get(catId))?.name || categoryName;
    unitName = (await db.unites.get(unitId))?.name || unitName;
    // Charger automatiquement les variantes liées à ce produit — mais seulement
    // celles réellement utilisées (au moins une ligne de stock/mouvement),
    // pas toutes les combinaisons jamais créées.
    const productVariants = await db.produits_variantes
      .filter((item) => (Array.isArray(item.produit_id) ? item.produit_id[0] : item.produit_id) === product.id)
      .toArray();
    const variantIds = new Set(productVariants.map((item) => item.id));
    const usedIds = new Set(
      (await db.stock_lignes
        .filter((line) => {
          const vid = Array.isArray(line.produit_variante_id) ? line.produit_variante_id[0] : line.produit_variante_id;
          return variantIds.has(vid) && line.active !== false && Number(line.quantite_totale) !== 0;
        })
        .toArray())
        .map((line) => (Array.isArray(line.produit_variante_id) ? line.produit_variante_id[0] : line.produit_variante_id)),
    );
    const tags = [...new Set(productVariants
      .filter((item) => usedIds.has(item.id))
      .map((item) => (Array.isArray(item.variante_tag_id) ? item.variante_tag_id[1] : ''))
      .filter(Boolean))];
    if (tags.length) selectedVariants = tags;
    openList = '';
  }

  function pickUnit(unit) { unitName = unit.name; openList = ''; }
  function pickCategory(cat) { categoryName = cat.name; openList = ''; }

  function addVariant(name) {
    const cleaned = (name || '').trim();
    if (!cleaned || selectedVariants.some((item) => norm(item) === norm(cleaned))) return;
    selectedVariants = [...selectedVariants, cleaned];
    variantQuery = '';
  }

  function removeVariant(name) {
    selectedVariants = selectedVariants.filter((item) => item !== name);
  }

  function onVariantKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (variantSuggestions.length === 1) addVariant(variantSuggestions[0].name);
      else if (variantCreatable) addVariant(variantQuery);
    } else if (event.key === 'Backspace' && !variantQuery && selectedVariants.length) {
      removeVariant(selectedVariants[selectedVariants.length - 1]);
    }
  }

  function closeListSoon() { setTimeout(() => { openList = ''; }, 140); }

  async function existingLinesByVariant() {
    const map = new Map();
    const product = await db.produits.where('reference').equalsIgnoreCase(reference.trim()).first();
    if (!product || !selectedZone) return map;
    const productVariants = await db.produits_variantes
      .filter((item) => (Array.isArray(item.produit_id) ? item.produit_id[0] : item.produit_id) === product.id)
      .toArray();
    for (const variant of productVariants) {
      const tagName = Array.isArray(variant.variante_tag_id) ? variant.variante_tag_id[1] : '';
      const line = await db.stock_lignes
        .filter((item) =>
          (Array.isArray(item.produit_variante_id) ? item.produit_variante_id[0] : item.produit_variante_id) === variant.id &&
          (Array.isArray(item.zone_id) ? item.zone_id[0] : item.zone_id) === selectedZone.id)
        .first();
      if (line) map.set(norm(tagName), { quantity: line.quantite_totale, printed: Number(line.quantite_imprimee) || 0 });
      map.set(`price:${norm(tagName)}`, { priceSale: variant.prix_vente, pricePurchase: variant.prix_achat });
    }
    return map;
  }

  async function prepareVariantRows() {
    if (!reference.trim()) reference = generateReference(descriptionSale);
    const names = selectedVariants.length ? selectedVariants : [''];
    const existing = await existingLinesByVariant();
    variantRows = names.map((name) => {
      const line = existing.get(norm(name));
      const price = existing.get(`price:${norm(name)}`);
      return {
        name,
        priceSale: price?.priceSale ?? '',
        pricePurchase: price?.pricePurchase ?? '',
        quantity: '',
        existing: line?.quantity ?? null,
        printed: line?.printed ?? 0,
      };
    });
    step = 4;
  }

  async function saveMovements() {
    if (!productValid || !rowsValid || !selectedEmployee || !selectedZone) return;
    const now = new Date().toISOString().replace('T', ' ').replace('Z', '').slice(0, 19);
    const rows = editableRows;
    await db.transaction('rw', db.mouvements, async () => {
      for (const row of rows) {
        await db.mouvements.add({
          uuid_client: createUuid(), statut: 'en_attente', created_at: Date.now(),
          ...zoneFields(), employe_id: selectedEmployee.id,
          quantite: Number(row.quantity), date_heure: now,
          produit: {
            reference: reference.trim(), description_vente: descriptionSale.trim(),
            description_achat: descriptionPurchase.trim(), categorie_nom: categoryName.trim(),
            unite_mesure_nom: unitName.trim(),
          },
          variante: {
            variante_tag_nom: row.name || null,
            // Prix optionnels : n'envoyer que ce qui a été réellement saisi,
            // pour ne jamais écraser un prix existant avec 0.
            ...(row.priceSale !== '' && row.priceSale !== null ? { prix_vente: Number(row.priceSale) } : {}),
            ...(row.pricePurchase !== '' && row.pricePurchase !== null ? { prix_achat: Number(row.pricePurchase) } : {}),
          },
        });
      }
    });
    success = `${rows.length} saisie${rows.length > 1 ? 's' : ''} sauvegardée${rows.length > 1 ? 's' : ''} ✓`;
    setTimeout(() => { success = ''; }, 4000);
    pending = await pendingCount();
    // Mémoriser la dernière catégorie saisie comme valeur par défaut du prochain produit.
    if (categoryName.trim()) {
      lastCategory = categoryName.trim();
      await db.meta.put({ key: 'last_category_name', value: lastCategory });
    }
    resetProduct(); step = 3;
    if (navigator.onLine && isSyncConfigured()) {
      try { await syncNow(); syncError = ''; await refreshLocalState(); } catch (error) { syncError = error.message; }
    }
  }

  function resetProduct() {
    descriptionSale = ''; descriptionPurchase = ''; reference = '';
    categoryName = lastCategory; // dernière catégorie saisie, comme valeur par défaut
    unitName = DEFAULT_UNIT; // "Pièce" par défaut
    variantQuery = ''; selectedVariants = []; variantRows = [];
  }

  /* ---- Consultation de la zone (popup) ---- */
  let consultOpen = false, consultRows = [], editKey = '', editValue = '';

  /* ---- Impression de tickets (popup) ---- */
  let printOpen = false, printRows = [], printing = false;
  $: canPrint = navigator.onLine && isSyncConfigured();

  async function buildConsultRows() {
    const zoneKey = selectedZone.id;
    const results = new Map();
    const cachedLines = await db.stock_lignes
      .filter((line) => (Array.isArray(line.zone_id) ? line.zone_id[0] : line.zone_id) === zoneKey && line.active !== false)
      .toArray();
    for (const line of cachedLines) {
      const variantId = Array.isArray(line.produit_variante_id) ? line.produit_variante_id[0] : line.produit_variante_id;
      const variant = await db.produits_variantes.get(variantId);
      if (!variant) continue;
      const product = await db.produits.get(Array.isArray(variant.produit_id) ? variant.produit_id[0] : variant.produit_id);
      const tagName = Array.isArray(variant.variante_tag_id) ? variant.variante_tag_id[1] : '';
      results.set(`v${variantId}`, {
        key: `v${variantId}`, variantId,
        name: product?.description_vente || product?.reference || '?',
        reference: product?.reference || '', tag: tagName,
        qty: line.quantite_totale,
        printed: Number(line.quantite_imprimee) || 0,
        // Impression possible seulement pour une ligne déjà confirmée par le
        // serveur (id + code-barres réels) — jamais pour une saisie encore
        // en attente de synchronisation (voir la branche `pendings` plus bas).
        stockLigneId: line.id, barcode: variant.code_barres,
        pending: false, payload: null,
      });
    }
    const pendings = await db.mouvements.where('statut').equals('en_attente').toArray();
    for (const movement of pendings) {
      const sameZone = selectedZone.local
        ? norm(movement.zone_code) === norm(selectedZone.code)
        : movement.zone_id === zoneKey;
      if (!sameZone) continue;
      let row = null;
      if (movement.produit_variante_id) row = results.get(`v${movement.produit_variante_id}`);
      else {
        const ref = norm(movement.produit?.reference);
        const tag = norm(movement.variante?.variante_tag_nom || '');
        row = [...results.values()].find((item) => norm(item.reference) === ref && norm(item.tag) === tag);
        if (!row) {
          const key = `p${ref}|${tag}`;
          row = results.get(key) || {
            key, variantId: null,
            name: movement.produit?.description_vente || movement.produit?.reference || '?',
            reference: movement.produit?.reference || '', tag: movement.variante?.variante_tag_nom || '',
            qty: 0, printed: 0, pending: true,
            payload: { produit: movement.produit, variante: movement.variante },
          };
          results.set(key, row);
        }
      }
      if (row) { row.qty += Number(movement.quantite) || 0; row.pending = true; }
    }
    consultRows = [...results.values()]
      .filter((row) => row.qty !== 0)
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }

  async function openConsult() { editKey = ''; await buildConsultRows(); consultOpen = true; }

  async function openPrint() {
    await buildConsultRows();
    // Seules les lignes confirmées par le serveur (id + code-barres réels) et
    // ayant un solde non imprimé peuvent être proposées à l'impression.
    printRows = consultRows
      .filter((row) => row.stockLigneId && row.barcode && Math.max(0, Math.round(row.qty - row.printed)) > 0)
      .map((row) => {
        const remaining = Math.max(0, Math.round(row.qty - row.printed));
        return { ...row, remaining, requested: remaining, wholeBatch: false };
      });
    printOpen = true;
  }

  function clampRequested(row) {
    const value = Math.round(Number(row.requested));
    row.requested = Number.isFinite(value) ? Math.min(Math.max(value, 0), row.remaining) : 0;
    printRows = printRows;
  }

  async function confirmPrint() {
    const targets = printRows.filter((row) => Number(row.requested) > 0);
    if (!targets.length || printing) return;
    if (!printSettings.relais_ip) {
      syncError = "Aucune imprimante centralisee configuree (Odoo > Parametres impression).";
      return;
    }
    printing = true;
    const relayUrl = `https://${printSettings.relais_ip}:${printSettings.relais_port || 9201}/print`;
    try {
      for (const row of targets) {
        const tspl = buildTicketTspl({
          description: row.name,
          reference: row.reference,
          zoneName: selectedZone?.code || '',
          barcode: row.barcode,
          widthMm: printSettings.tspl_largeur_mm,
          heightMm: printSettings.tspl_hauteur_mm,
          barcodeOffsetX: printSettings.tspl_barcode_decalage_x,
          fontCharWidth: printSettings.tspl_largeur_caractere,
          quantity: Number(row.requested),
        });
        const response = await fetch(relayUrl, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: tspl });
        if (!response.ok) throw new Error(`Relais d'impression : ${await response.text()}`);
      }
    } catch (error) {
      printing = false;
      syncError = `Impression echouee (${error.message}). Verifiez que le relais est demarre et que cet appareil a bien accepte son certificat une premiere fois.`;
      return;
    }
    // "Solde complet" : le nombre d'etiquettes VRAIMENT imprimees (row.requested)
    // peut differer de la quantite a marquer comme geree cote serveur -- ex. un
    // carton de 1000 petites pieces identiques : on imprime 1 seule etiquette
    // pour tout le carton, mais les 1000 doivent disparaitre de "a imprimer",
    // pas seulement 1 (sinon 999 restent indefiniment en attente).
    const prints = targets.map((row) => ({
      stock_ligne_id: row.stockLigneId,
      quantity: row.wholeBatch ? row.remaining : Number(row.requested),
    }));
    printOpen = false;
    try {
      await syncNow(prints);
      syncError = '';
      await refreshLocalState();
      if (selectedZone) await buildConsultRows();
    } catch (error) {
      syncError = error.message;
    } finally {
      printing = false;
    }
  }

  async function applyDelta(row, newTotal) {
    const delta = Number(newTotal) - row.qty;
    if (!Number.isFinite(delta) || delta === 0) { editKey = ''; return; }
    // La part déjà imprimée est intouchable : on peut ajouter, jamais descendre
    // en dessous de la quantité dont les étiquettes sont déjà collées.
    if (row.printed > 0 && Number(newTotal) < row.printed) {
      syncError = `Minimum ${row.printed} : cette quantité est déjà imprimée et étiquetée.`;
      return;
    }
    const now = new Date().toISOString().replace('T', ' ').replace('Z', '').slice(0, 19);
    const movement = {
      uuid_client: createUuid(), statut: 'en_attente', created_at: Date.now(),
      ...zoneFields(), employe_id: selectedEmployee.id,
      quantite: delta, date_heure: now,
    };
    if (row.variantId) movement.produit_variante_id = row.variantId;
    else { movement.produit = row.payload.produit; movement.variante = row.payload.variante; }
    await db.mouvements.add(movement);
    editKey = '';
    pending = await pendingCount();
    if (navigator.onLine && isSyncConfigured()) {
      try { await syncNow(); syncError = ''; await refreshLocalState(); } catch (error) { syncError = error.message; }
    }
    await buildConsultRows();
  }

  async function deleteConsultRow(row) {
    if (!confirm(`Supprimer « ${row.name}${row.tag ? ` / ${row.tag}` : ''} » de cette zone (quantité remise à zéro) ?`)) return;
    await applyDelta(row, 0);
  }
</script>

<main class="shell">
  <header class="topbar">
    <div class="brand"><img class="brand-logo" src="/logo.png" alt="SoonTech" /><span class="badge-terrain">Terrain</span></div>
    {#if selectedEmployee && step > 1}
      <button class="employee-chip" on:click={changeEmployee} title="Changer d'utilisateur">
        <span class="avatar">{selectedEmployee.name.slice(0, 1)}</span>
        <span class="employee-name">{selectedEmployee.name}</span>
        <span class="change-hint">changer</span>
      </button>
    {/if}
  </header>

  <div class="syncbar" class:warning={pending > 0}>
    <span class="sync-state"><span class="dot" class:online={navigator.onLine}></span>{navigator.onLine ? 'En ligne' : 'Hors ligne'} · {pending} en attente</span>
    <small>{lastSync ? `Sync : ${new Date(`${lastSync}Z`).toLocaleString('fr')}` : 'Jamais synchronisé'}</small>
    {#if pending > 0}
      <button type="button" class="force-sync-btn" disabled={forcingSync} on:click={forceSyncNow}>
        {forcingSync ? '⏳ Envoi…' : '🔄 Forcer l\'envoi'}
      </button>
    {/if}
  </div>
  {#if errored > 0}
    <button type="button" class="status error tappable" on:click={showErroredDetail}>
      ⚠️ {errored} saisie{errored > 1 ? 's' : ''} en erreur, non renvoyée{errored > 1 ? 's' : ''} — contactez l'admin
      <span class="tap-hint">Toucher pour le détail</span>
    </button>
  {/if}

  {#if success}
    <div class="toast" transition:fly={{ y: -16, duration: 240 }}>{success}</div>
  {/if}

  {#if fatalError}
    <button type="button" class="status error tappable" on:click={() => showErrorDetail(fatalError)}>
      <strong>Impossible de démarrer</strong><span>{fatalError}</span><span class="tap-hint">Toucher pour le détail</span>
    </button>
  {:else if !ready}
    <div class="card loading"><span class="spinner"></span> Préparation…</div>
  {:else}
    <nav class="steps" aria-label="Progression">
      {#each ['Identité', 'Zone', 'Produit', 'Quantités'] as label, index}
        <span class="step-dot" class:active={step === index + 1} class:done={step > index + 1}>{label}</span>
      {/each}
    </nav>

    {#if syncError}
      <button type="button" class="status error tappable" transition:fade on:click={() => showErrorDetail(syncError)}>
        Données conservées localement. {syncError}
        <span class="tap-hint">Toucher pour le détail</span>
      </button>
    {/if}

    {#if step === 1}
      <section class="card" in:fly={{ y: 16, duration: 240 }}>
        <h1>Qui êtes-vous ?</h1>
        <label for="employee">Votre nom</label>
        <select id="employee" bind:value={employeeId}>
          <option value="">Choisir votre nom</option>
          {#each employees as employee}<option value={employee.id}>{employee.name}</option>{/each}
        </select>
        {#if !employees.length}<p class="field-error">Première synchronisation nécessaire pour charger les employés.</p>{/if}
        <p class="hint">Votre nom restera mémorisé sur cet appareil.</p>
        <button class="primary" disabled={!selectedEmployee} on:click={confirmEmployee}>Continuer</button>
      </section>

    {:else if step === 2}
      <section class="card" in:fly={{ y: 16, duration: 240 }}>
        <h1>Quel emplacement ?</h1>
        <label for="zone">Code ou nom de zone</label>
        <div class="suggest-wrap">
          <input id="zone" bind:value={zoneQuery} autocomplete="off" placeholder="Ex. A-12"
            on:focus={() => { openList = 'zone'; }} on:input={() => { openList = 'zone'; selectedZone = null; }}
            on:blur={closeListSoon}
            on:keydown={(event) => event.key === 'Enter' && chooseZone()} />
          {#if openList === 'zone' && zoneSuggestions.length}
            <ul class="suggest" transition:fade={{ duration: 120 }}>
              {#each zoneSuggestions as zone}
                <li><button type="button" on:mousedown|preventDefault={() => pickZone(zone)}>
                  <strong>{zone.code}</strong><span>{zone.name}</span>
                </button></li>
              {/each}
            </ul>
          {/if}
        </div>
        {#if zoneQuery.trim() && !zoneExact}
          <p class="field-error">Cet emplacement n'existe pas encore</p>
          <button type="button" class="create-zone" on:click={createLocalZone}>+ Créer l'emplacement «{zoneQuery.trim()}»</button>
        {/if}
        <button class="primary" disabled={!zoneQuery.trim() || !zoneExact} on:click={chooseZone}>Valider l'emplacement</button>
      </section>

    {:else if step === 3}
      <section class="card" in:fly={{ y: 16, duration: 240 }}>
        <button class="back" on:click={changeZone}>← Passer à une autre zone</button>
        <h1>Nouveau produit</h1>
        <div class="zone-row">
          <button type="button" class="location" on:click={changeZone} title="Changer d'emplacement">
            📍 {selectedZone.code} — {selectedZone.name}
            {#if selectedZone.local}<span class="loc-new">NOUVEAU</span>{/if}
            <span class="loc-change">changer</span>
          </button>
          <button type="button" class="consult-btn" on:click={openConsult}>📋 Consulter la zone</button>
          <button type="button" class="consult-btn" disabled={!canPrint} title={canPrint ? '' : 'Nécessite une connexion internet'} on:click={openPrint}>🖨️ Imprimer</button>
        </div>

        <label for="sale-desc">Description de vente *</label>
        <div class="suggest-wrap">
          <textarea id="sale-desc" rows="2" bind:value={descriptionSale}
            on:focus={() => { openList = 'desc'; }} on:input={() => { openList = 'desc'; }} on:blur={closeListSoon}></textarea>
          {#if openList === 'desc' && descSuggestions.length}
            <ul class="suggest" transition:fade={{ duration: 120 }}>
              {#each descSuggestions as product}
                <li><button type="button" on:mousedown|preventDefault={() => pickProduct(product)}>
                  <strong>{product.reference}</strong><span>{product.description_vente}</span>
                </button></li>
              {/each}
            </ul>
          {/if}
        </div>

        <label for="purchase-desc">Description d'achat</label>
        <textarea id="purchase-desc" rows="2" bind:value={descriptionPurchase}></textarea>

        <div class="two-cols">
          <div>
            <label for="reference">Référence</label>
            <div class="suggest-wrap">
              <input id="reference" bind:value={reference} autocomplete="off" placeholder="Généré automatiquement si vide"
                on:focus={() => { openList = 'ref'; }} on:input={() => { openList = 'ref'; }} on:blur={closeListSoon} />
              {#if openList === 'ref' && refSuggestions.length}
                <ul class="suggest" transition:fade={{ duration: 120 }}>
                  {#each refSuggestions as product}
                    <li><button type="button" on:mousedown|preventDefault={() => pickProduct(product)}>
                      <strong>{product.reference}</strong><span>{product.description_vente}</span>
                    </button></li>
                  {/each}
                </ul>
              {/if}
            </div>
          </div>
          <div>
            <label for="unit">Unité *</label>
            <div class="suggest-wrap">
              <input id="unit" bind:value={unitName} autocomplete="off" placeholder="Pièce, kg…"
                on:focus={() => { openList = 'unit'; }} on:input={() => { openList = 'unit'; }} on:blur={closeListSoon} />
              {#if openList === 'unit' && unitSuggestions.length}
                <ul class="suggest" transition:fade={{ duration: 120 }}>
                  {#each unitSuggestions as unit}
                    <li><button type="button" on:mousedown|preventDefault={() => pickUnit(unit)}><strong>{unit.name}</strong></button></li>
                  {/each}
                </ul>
              {/if}
            </div>
          </div>
        </div>

        <label for="category">Catégorie *</label>
        <div class="suggest-wrap">
          <input id="category" bind:value={categoryName} autocomplete="off"
            on:focus={() => { openList = 'cat'; }} on:input={() => { openList = 'cat'; }} on:blur={closeListSoon} />
          {#if openList === 'cat' && catSuggestions.length}
            <ul class="suggest" transition:fade={{ duration: 120 }}>
              {#each catSuggestions as cat}
                <li><button type="button" on:mousedown|preventDefault={() => pickCategory(cat)}><strong>{cat.name}</strong></button></li>
              {/each}
            </ul>
          {/if}
        </div>

        <label for="variants">Variantes</label>
        <div class="tags-field suggest-wrap" class:focused={openList === 'variant'}>
          {#each selectedVariants as name (name)}
            <span class="tag" transition:fade={{ duration: 120 }}>
              {name}
              <button type="button" class="tag-remove" on:click={() => removeVariant(name)} aria-label={`Retirer ${name}`}>×</button>
            </span>
          {/each}
          <input id="variants" class="tag-input" bind:value={variantQuery} autocomplete="off"
            placeholder={selectedVariants.length ? '' : 'Rouge, XL, 500g…'}
            on:focus={() => { openList = 'variant'; }} on:input={() => { openList = 'variant'; }}
            on:blur={closeListSoon} on:keydown={onVariantKeydown} />
          {#if openList === 'variant' && (variantSuggestions.length || variantCreatable)}
            <ul class="suggest" transition:fade={{ duration: 120 }}>
              {#each variantSuggestions as tag}
                <li><button type="button" on:mousedown|preventDefault={() => addVariant(tag.name)}><strong>{tag.name}</strong></button></li>
              {/each}
              {#if variantCreatable}
                <li><button type="button" class="create" on:mousedown|preventDefault={() => addVariant(variantQuery)}>+ Créer «{variantQuery.trim()}»</button></li>
              {/if}
            </ul>
          {/if}
        </div>
        <p class="hint">Touchez une suggestion pour l'ajouter, ou tapez puis Entrée pour créer.</p>

        <button class="primary" disabled={!productValid} on:click={prepareVariantRows}>Prix et quantité →</button>
      </section>

    {:else}
      <section class="card" in:fly={{ y: 16, duration: 240 }}>
        <button class="back" on:click={() => { step = 3; }}>← Produit</button>
        <h1>Prix et quantité</h1>
        <p class="location">📍 {selectedZone.code} · {reference}</p>

        <div class="grid-table" role="table" aria-label="Prix et quantités par variante">
          <div class="grid-head" role="row">
            <span>Variante</span><span>Prix achat</span><span>Prix vente</span><span>Quantité *</span>
          </div>
          {#each variantRows as row, index (row.name)}
            <div class="grid-row" role="row" in:fade={{ duration: 160, delay: index * 40 }}>
              <span class="variant-name">
                {row.name || 'Sans variante'}
                {#if row.existing !== null}<span class="badge">déjà : {row.existing}</span>{/if}
                {#if row.printed > 0}<span class="badge closed-badge">🖨 {row.printed} imprimé</span>{/if}
              </span>
              <span data-label="Prix achat"><input id={`purchase-${index}`} type="number" inputmode="decimal" min="0" step="0.01" bind:value={row.pricePurchase} /></span>
              <span data-label="Prix vente"><input id={`sale-${index}`} type="number" inputmode="decimal" min="0" step="0.01" bind:value={row.priceSale} /></span>
              <span data-label="Quantité *"><input id={`quantity-${index}`} type="number" inputmode="decimal" step="any" bind:value={row.quantity} placeholder="0" /></span>
            </div>
          {/each}
        </div>
        {#if variantRows.some((row) => row.quantity !== '' && (!Number(row.quantity) || Math.abs(Number(row.quantity)) > 1000000))}
          <p class="field-error">Chaque quantité doit être non nulle (max 1 000 000).</p>
        {/if}
        <button class="primary" disabled={!rowsValid} on:click={saveMovements}>Sauvegarder</button>
      </section>
    {/if}
  {/if}

  {#if consultOpen && selectedZone}
    <div class="modal-overlay" transition:fade={{ duration: 130 }} on:click|self={() => { consultOpen = false; }} role="presentation">
      <div class="modal" transition:fly={{ y: 22, duration: 200 }} role="dialog" aria-label="Contenu de la zone">
        <header class="modal-head">
          <h2>📋 {selectedZone.code} — contenu</h2>
          <button class="close" on:click={() => { consultOpen = false; }} aria-label="Fermer">×</button>
        </header>
        <div class="modal-body">
          {#if !consultRows.length}
            <p class="hint">Aucune saisie dans cette zone pour l'instant.</p>
          {:else}
            {#each consultRows as row (row.key)}
              <div class="consult-row">
                <div class="consult-info">
                  <strong>{row.name}</strong>
                  <span class="consult-meta">
                    {row.reference}{row.tag ? ` · ${row.tag}` : ''}
                    {#if row.printed > 0}<span class="badge closed-badge">🖨 {row.printed} imprimé</span>{/if}
                    {#if row.pending}<span class="badge">en attente de sync</span>{/if}
                  </span>
                </div>
                {#if editKey === row.key}
                  <div class="consult-edit">
                    <input type="number" inputmode="decimal" bind:value={editValue} placeholder={String(row.qty)} min={row.printed || undefined} />
                    <button type="button" class="mini-ok" on:click={() => applyDelta(row, editValue)}>OK</button>
                    <button type="button" class="mini-cancel" on:click={() => { editKey = ''; }}>✕</button>
                  </div>
                {:else}
                  <div class="consult-actions">
                    <span class="consult-qty">{row.qty}</span>
                    <button type="button" class="mini-edit" on:click={() => { editKey = row.key; editValue = String(row.qty); }}>✎</button>
                    {#if !row.printed}
                      <button type="button" class="mini-delete" on:click={() => deleteConsultRow(row)}>🗑</button>
                    {/if}
                  </div>
                {/if}
              </div>
            {/each}
          {/if}
        </div>
      </div>
    </div>
  {/if}

  {#if printOpen}
    <div class="modal-overlay" transition:fade={{ duration: 130 }} on:click|self={() => { printOpen = false; }} role="presentation">
      <div class="modal" transition:fly={{ y: 22, duration: 200 }} role="dialog" aria-label="Imprimer les tickets">
        <header class="modal-head">
          <h2>🖨️ {selectedZone?.code} — impression</h2>
          <button class="close" on:click={() => { printOpen = false; }} aria-label="Fermer">×</button>
        </header>
        <div class="modal-body">
          {#if !printRows.length}
            <p class="hint">Rien à imprimer dans cette zone : tout est déjà étiqueté.</p>
          {:else}
            <p class="hint">Quantité par défaut = solde non imprimé. Modifiable — ex. mettre <strong>1</strong> pour un carton contenant beaucoup de petites pièces identiques. Dans ce cas, cocher « Représente tout le solde » : une seule étiquette est imprimée, mais tout le solde disparaît de « à imprimer » (sinon le reste resterait indéfiniment en attente).</p>
            {#each printRows as row (row.key)}
              <div class="consult-row">
                <div class="consult-info">
                  <strong>{row.name}</strong>
                  <span class="consult-meta">{row.reference}{row.tag ? ` · ${row.tag}` : ''} · solde {row.remaining}</span>
                  <label class="check whole-batch">
                    <input type="checkbox" bind:checked={row.wholeBatch} />
                    Représente tout le solde ({row.remaining})
                  </label>
                </div>
                <div class="consult-edit">
                  <input type="number" inputmode="numeric" min="0" max={row.remaining} bind:value={row.requested} on:change={() => clampRequested(row)} />
                </div>
              </div>
            {/each}
            <button class="primary" disabled={printing || !printRows.some((row) => Number(row.requested) > 0)} on:click={confirmPrint}>
              {printing ? '⏳ Impression…' : `🖨️ Imprimer (${printRows.reduce((sum, row) => sum + (Math.max(0, Math.round(Number(row.requested) || 0))), 0)})`}
            </button>
          {/if}
        </div>
      </div>
    </div>
  {/if}

  <img src="/logo.png" alt="SoonTech" class="corner-badge" />

  {#if errorDetailOpen}
    <div class="modal-overlay" transition:fade={{ duration: 130 }} on:click|self={() => { errorDetailOpen = false; }} role="presentation">
      <div class="modal" transition:fly={{ y: 22, duration: 200 }} role="dialog" aria-label="Détail de l'erreur">
        <header class="modal-head">
          <h2>⚠️ Détail de l'erreur</h2>
          <button class="close" on:click={() => { errorDetailOpen = false; }} aria-label="Fermer">×</button>
        </header>
        <div class="modal-body">
          <p class="error-detail-text">{errorDetailText}</p>
          <button type="button" class="consult-btn" on:click={copyErrorDetail}>{errorDetailCopied ? '✓ Copié' : '📋 Copier le texte'}</button>
        </div>
      </div>
    </div>
  {/if}
</main>
